"use client";
import React, { useCallback, useMemo } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import { Button, Col, Form, Row } from "react-bootstrap";
import Modal from "react-bootstrap/Modal";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

// ✅ Updated initial field
const initialField = { shopify_market: "", pim_locale: "" };

// ✅ Updated validation schema
const validationSchema = Yup.object({
  mappings: Yup.array().of(
    Yup.object().shape({
      shopify_market: Yup.string().required("Shopify market is required"),
      pim_locale: Yup.string().when("shopify_market", {
        is: (val) => !!val,
        then: (schema) => schema.required("PIM locale is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
    })
  ),
});

function DataFilterPopup({ toggle, setToggle }) {
  const formik = useFormik({
    initialValues: {
      mappings: [initialField],
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: (values) => {
      console.log("Submitted values:", values);
      setToggle(false);
    },
  });

  const { values, errors, touched, handleChange, handleSubmit } = formik;

  const handleAddFields = useCallback(() => {
    const updatedMappings = [...values.mappings, initialField];
    formik.setFieldValue("mappings", updatedMappings);
  }, [values.mappings, formik]);

  const handleRemoveFields = useCallback(
    (index) => {
      const updatedMappings = values.mappings.filter((_, i) => i !== index);
      formik.setFieldValue("mappings", updatedMappings);
    },
    [values.mappings, formik]
  );

  // Memoize the last index
  const lastIndex = useMemo(
    () => values.mappings.length - 1,
    [values.mappings.length]
  );

  return (
    <>
      <Modal
        show={toggle}
        onHide={() => setToggle(false)}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        dialogClassName="custom-modal-width"
        centered
      >
        <div className="popup-content bg-white p-6 rounded-lg shadow-lg p-5 rounded-3">
          <h5 className="fw-bold mb-4 border-bottom border-gray">
            Edit Markets Pricing
          </h5>

          <Row className="mb-3">
            <Col sm={4}>
              <strong>Shopify Market</strong>
            </Col>
            <Col sm={3}>
              <strong>Currency</strong>
            </Col>
            <Col sm={4}>
              <strong>PIM Locale</strong>
            </Col>
          </Row>

          {values.mappings.map((inputField, index) => (
            <React.Fragment key={index}>
              <Row>
                {/* Shopify Market Dropdown */}
                <Col sm={4}>
                  <Form.Group controlId={`mappings.${index}.shopify_market`}>
                    <Form.Select
                      name={`mappings[${index}].shopify_market`}
                      value={inputField.shopify_market}
                      onChange={handleChange}
                      isInvalid={
                        !!errors.mappings?.[index]?.shopify_market &&
                        touched.mappings?.[index]?.shopify_market
                      }
                    >
                      <option value="">Select a Shopify market</option>
                      <option value="Market 1">Market 1</option>
                      <option value="Market 2">Market 2</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.mappings?.[index]?.shopify_market}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {/* Compare arrows icon */}
                <Col sm={3}>
                  <Form.Group className="mb-3 d-flex align-items-center position-relative">
                    <Form.Control
                      type="text"
                      name="curency"
                      value="USD"
                      readOnly
                    />
                  </Form.Group>
                </Col>

                {/* PIM Locale Dropdown */}
                <Col sm={4}>
                  <Form.Group controlId={`mappings.${index}.pim_locale`}>
                    <Form.Select
                      name={`mappings[${index}].pim_locale`}
                      value={inputField.pim_locale}
                      onChange={handleChange}
                      isInvalid={
                        !!errors.mappings?.[index]?.pim_locale &&
                        touched.mappings?.[index]?.pim_locale
                      }
                    >
                      <option value="">Select a PIM locale</option>
                      <option value="Locale 1">Locale 1</option>
                      <option value="Locale 2">Locale 2</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.mappings?.[index]?.pim_locale}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {/* Remove button */}
                <Col sm={1} className="ps-0 pt-1">
                  {values.mappings.length > 1 && (
                    <Button
                      variant="link"
                      type="button"
                      onClick={() => handleRemoveFields(index)}
                      className="channelActionBtn p-0"
                    >
                      <DeleteOutlineIcon style={{ fontSize: "30px" }} />
                    </Button>
                  )}
                </Col>
              </Row>

              {/* Add button only on last row */}
              {index === lastIndex && (
                <Row>
                  <Col
                    sm={10}
                    className="d-flex justify-content-center mt-3 ms-4"
                  >
                    <Button
                      type="button"
                      onClick={handleAddFields}
                      style={{
                        border: "1px solid lightgray",
                        borderRadius: "7px",
                        padding: "8px 12px",
                        fontSize: "14px",
                        backgroundColor: "#ffffff",
                        color: "#000000",
                      }}
                    >
                      Add new mapping
                    </Button>
                  </Col>
                </Row>
              )}
            </React.Fragment>
          ))}
        </div>

        <Modal.Footer>
          <button
            onClick={() => setToggle(false)}
            className="modal_cancel_button modal_button model_footer absolute top-2 right-2 text-gray-600 hover:text-black"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="modal_save_button modal_button model_footer absolute top-2 right-2 text-gray-600 hover:text-black"
          >
            Save
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default DataFilterPopup;
